/**
 * Turn a user-supplied ad name (custom rename or template snapshot) into a
 * filesystem-safe download filename. Collapses anything outside [a-z0-9_-]
 * into single dashes, trims leading/trailing dashes, lowercases the head,
 * caps the length, and falls back to "ad" when nothing safe remains.
 *
 * Task #2314 — convention ad names end in "{YYMMDD}{Letter}" (e.g. "260811A"),
 * the segment media buyers rely on, and routinely exceed the length cap. When
 * the name carries that tail (optionally followed by an already-appended
 * "-9x16"-style ratio suffix):
 *   - the tail keeps its ORIGINAL case (the run letter stays uppercase);
 *   - length-capping trims the head of the name, never the tail.
 * Names without a parseable tail behave exactly as before (lowercased,
 * head-kept truncation), so short/non-convention names are unchanged.
 */

const MAX_LENGTH = 120;

// Trailing date code + run letter, plus an optional ratio suffix that some
// callers append before sanitizing (e.g. "…_260811A-9x16").
const DATE_CODE_TAIL_RE = /(\d{6})([A-Za-z]+)((?:-\d+x\d+)?)$/;

export function sanitizeFilename(name: string, fallback = "ad"): string {
  const cleaned = name
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!cleaned) return fallback;

  const m = DATE_CODE_TAIL_RE.exec(cleaned);
  if (!m) {
    return cleaned.toLowerCase().slice(0, MAX_LENGTH) || fallback;
  }

  // Preserve the tail's case; lowercase everything before it (legacy behavior).
  const tail = m[1] + m[2] + m[3].toLowerCase();
  let head = cleaned.slice(0, m.index).toLowerCase();

  if (head.length + tail.length > MAX_LENGTH) {
    // Trim the head from its end so the date code + letter always survives,
    // reserving one char to keep a separator between head and tail.
    head = head
      .slice(0, Math.max(0, MAX_LENGTH - tail.length - 1))
      .replace(/[-_]+$/, "");
    if (head) head += "-";
  }

  return head + tail || fallback;
}

/**
 * Task #1572 / #2314 — build unique zip entry filenames for a batch download.
 * The same ad in multiple aspect ratios shares a base name; the ratio suffix
 * is appended only where needed, and any residual duplicates get a numeric
 * suffix. Suffixes go AFTER sanitizing so long names (already tail-preserved
 * by sanitizeFilename) still get unique, non-colliding filenames.
 */
export function buildZipFileNames(
  items: Array<{ name: string; aspectRatio: string }>,
  ext = "png",
): string[] {
  const baseCounts = new Map<string, number>();
  for (const it of items) {
    const b = sanitizeFilename(it.name);
    baseCounts.set(b, (baseCounts.get(b) ?? 0) + 1);
  }
  const used = new Map<string, number>();
  return items.map((it) => {
    let baseName = sanitizeFilename(it.name);
    if ((baseCounts.get(baseName) ?? 0) > 1) {
      baseName = `${baseName}-${it.aspectRatio.replace(":", "x")}`;
    }
    const dupCount = used.get(baseName) ?? 0;
    used.set(baseName, dupCount + 1);
    return dupCount === 0 ? `${baseName}.${ext}` : `${baseName}-${dupCount + 1}.${ext}`;
  });
}
